import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";

import { API_ENDPOINT } from "../../../services/api/resources";
import { openNotificationPanel } from "../../../utils/notificationEvents";

const POLL_INTERVAL_MS = 3000;
const TOAST_AUTO_HIDE_MS = 6500;
const PUBLIC_ROUTE_PREFIXES = [
  "/login",
  "/ai_dialogue",
  "/home",
  "/privacy",
  "/cookie",
  "/buyer_organization_signup",
  "/vendor_signup",
  "/unified_onboarding",
  "/poc_vendor_signup",
  "/poc_Invoice_Automation",
  "/rfi_signup",
  "/unregistered_vendor/register",
  "/free-rfq-document-extraction",
  "/public-submission",
];

const normalizeText = (value) => String(value ?? "").trim();

const normalizeDate = (item) =>
  item?.created ||
  item?.created_at ||
  item?.created_on ||
  item?.created_date ||
  item?.date ||
  "";

const normalizeReadStatus = (status) => {
  if (status === true || status === false) return status;

  const normalizedStatus = normalizeText(status).toLowerCase();
  if (
    status === 1 ||
    status === "1" ||
    normalizedStatus === "true" ||
    normalizedStatus === "read" ||
    normalizedStatus === "seen" ||
    normalizedStatus === "viewed"
  ) {
    return true;
  }

  if (
    status === 0 ||
    status === "0" ||
    normalizedStatus === "false" ||
    normalizedStatus === "unread" ||
    normalizedStatus === "new"
  ) {
    return false;
  }

  return true;
};

const getResolvedNotificationStatus = (item = {}) => {
  const statusKeys = [
    "status",
    "read_status",
    "is_read",
    "isRead",
    "read",
    "seen",
    "viewed",
    "notification_status",
    "notification_read_status",
  ];

  for (const key of statusKeys) {
    const value = item?.[key];
    if (value === undefined || value === null || normalizeText(value) === "") {
      continue;
    }
    return normalizeReadStatus(value);
  }

  const unreadKeys = ["unread", "is_unread", "isUnread"];
  for (const key of unreadKeys) {
    const value = item?.[key];
    const normalizedValue = normalizeText(value).toLowerCase();
    if (value === true || value === 1 || normalizedValue === "true") {
      return false;
    }
    if (value === false || value === 0 || normalizedValue === "false") {
      return true;
    }
  }

  return true;
};

const getNotificationId = (item = {}) =>
  item?.id ||
  item?.notify_id ||
  item?.notification_id ||
  item?.message_id ||
  item?.token ||
  item?.notification_token ||
  item?.message_token ||
  "";

const getNotificationKey = (item = {}, fallbackKey = "") => {
  const primaryId = normalizeText(getNotificationId(item));
  if (primaryId) return `notification:${primaryId}`;

  return [
    "notification:fallback",
    normalizeText(item?.message || item?.body || item?.description || item?.text),
    normalizeText(normalizeDate(item)),
    normalizeText(item?.buyer || item?.buyer_token),
    normalizeText(item?.vendor || item?.vendor_token),
    normalizeText(item?.rfq || item?.rfq_id || item?.rfq_token),
    fallbackKey,
  ].join("|");
};

const formatTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getNotificationText = (item = {}) =>
  normalizeText(
    item?.message || item?.body || item?.description || item?.comments || item?.text,
  );

const getNotificationTitle = (item = {}) =>
  normalizeText(
    item?.title ||
      item?.subject ||
      item?.heading ||
      item?.notification_title ||
      item?.name ||
      "New notification",
  );

const buildNotificationToast = (item, index, source) => {
  const message = getNotificationText(item);
  if (!message || getResolvedNotificationStatus(item) !== false) return null;

  const createdRaw = normalizeDate(item);
  return {
    id: `${source}:${getNotificationKey(item, `${createdRaw}-${index}`)}`,
    type: "notification",
    title: getNotificationTitle(item),
    message,
    time: formatTime(createdRaw),
  };
};

const snackbarSlide = (props) => <Slide {...props} direction="left" />;

const GlobalNotificationToast = () => {
  const location = useLocation();
  const loginData = useSelector((state) => state.loginData.value);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [queue, setQueue] = useState([]);
  const [activeToast, setActiveToast] = useState(null);
  const [open, setOpen] = useState(false);
  const knownToastIdsRef = useRef(new Set());
  const didSeedRef = useRef(false);

  const authToken = loginData?.auth_token || loginData?.token || "";
  const accessType = normalizeText(loginData?.access_type).toLowerCase();
  const buyerToken =
    loginData?.user?.token ||
    loginData?.buyer_token ||
    loginData?.organization_token ||
    "";
  const userToken =
    loginData?.user?.user_token ||
    loginData?.user_token ||
    loginData?.user?.token ||
    "";
  const vendorToken =
    loginData?.user?.token ||
    loginData?.vendor_contact_token ||
    loginData?.vendor_token ||
    "";
  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  ) || location.pathname === "/";
  const canShowNotifications = Boolean(authToken && isLoggedIn && !isPublicRoute);

  const scope = useMemo(() => {
    const isBuyerOrAdmin = accessType === "buyer" || accessType === "admin";
    const isUser = accessType === "user";
    const isVendor =
      accessType === "vendor admin" || accessType === "vendor contact";

    return {
      isBuyerOrAdmin,
      isUser,
      isVendor,
      notification:
        isBuyerOrAdmin && buyerToken
          ? { key: "buyer_token", value: buyerToken }
          : isUser && userToken
            ? { key: "user_token", value: userToken }
            : isVendor && vendorToken
              ? { key: "vendor_token", value: vendorToken }
              : null,
    };
  }, [accessType, buyerToken, userToken, vendorToken]);

  useEffect(() => {
    knownToastIdsRef.current = new Set();
    didSeedRef.current = false;
    setQueue([]);
    setActiveToast(null);
    setOpen(false);
  }, [authToken, accessType, buyerToken, userToken, vendorToken]);

  useEffect(() => {
    if (!canShowNotifications) return undefined;

    let isMounted = true;
    let isPolling = false;

    const fetchGeneralNotifications = async () => {
      const formData = new FormData();
      if (scope.notification?.key && scope.notification?.value) {
        formData.append(scope.notification.key, scope.notification.value);
      }

      const response = await axios.post(API_ENDPOINT.getNotifications, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return (response?.data?.response || [])
        .map((item, index) => buildNotificationToast(item, index, "general"))
        .filter(Boolean);
    };

    const fetchApprovalNotifications = async () => {
      if (!scope.isBuyerOrAdmin && !scope.isUser) return [];

      const formData = new FormData();
      if (scope.isBuyerOrAdmin && buyerToken) {
        formData.append("buyer_token", buyerToken);
      }
      if (scope.isUser && userToken) {
        formData.append("user_token", userToken);
      }

      const response = await axios.post(
        API_ENDPOINT.approvalNotifications,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const notifications =
        response?.data?.notifications ||
        (response?.data?.status !== "error" && response?.data);

      return (Array.isArray(notifications)
        ? notifications
        : notifications
          ? [notifications]
          : []
      )
        .map((item, index) => buildNotificationToast(item, index, "approval"))
        .filter(Boolean);
    };

    const pollForNewItems = async () => {
      if (isPolling) return;
      isPolling = true;

      try {
        const results = await Promise.allSettled([
          fetchGeneralNotifications(),
          fetchApprovalNotifications(),
        ]);

        if (!isMounted) return;

        const nextToasts = results
          .flatMap((result) =>
            result.status === "fulfilled" ? result.value : [],
          )
          .filter((toast) => toast?.id);

        if (!didSeedRef.current) {
          nextToasts.forEach((toast) => knownToastIdsRef.current.add(toast.id));
          didSeedRef.current = true;
          return;
        }

        const unseenToasts = nextToasts.filter((toast) => {
          if (knownToastIdsRef.current.has(toast.id)) return false;
          knownToastIdsRef.current.add(toast.id);
          return true;
        });

        if (unseenToasts.length > 0) {
          setQueue((prev) => [...prev, ...unseenToasts].slice(-5));
        }
      } finally {
        isPolling = false;
      }
    };

    pollForNewItems();
    const intervalId = window.setInterval(pollForNewItems, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [authToken, buyerToken, canShowNotifications, scope, userToken]);

  useEffect(() => {
    if (!activeToast && queue.length > 0) {
      setActiveToast(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    }
  }, [activeToast, queue]);

  const handleClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleToastClick = () => {
    openNotificationPanel();
    setOpen(false);
  };

  const handleCloseClick = (event) => {
    event.stopPropagation();
    handleClose();
  };

  const handleExited = () => {
    setActiveToast(null);
  };

  if (!activeToast) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={TOAST_AUTO_HIDE_MS}
      onClose={handleClose}
      TransitionComponent={snackbarSlide}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      sx={{
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        zIndex: 1600,
      }}
    >
      <Alert
        icon={false}
        severity="info"
        onClick={handleToastClick}
        sx={{
          width: { xs: "calc(100vw - 24px)", sm: 380 },
          maxWidth: "100%",
          p: 0,
          cursor: "pointer",
          borderRadius: "14px",
          overflow: "hidden",
          border: "1px solid rgba(203, 213, 225, 0.9)",
          background: "#fff",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
          fontFamily: "gilroyMedium, Arial, sans-serif",
          "& .MuiAlert-message": {
            width: "100%",
            p: 0,
          },
        }}
      >
        <Stack direction="row" spacing={1.4} sx={{ p: "14px 14px 13px" }}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: "#0F766E",
              color: "#fff",
              flex: "0 0 auto",
              boxShadow: "0 8px 18px rgba(15, 23, 42, 0.16)",
            }}
          >
            <NotificationsRoundedIcon sx={{ fontSize: 22 }} />
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                sx={{
                  color: "#0F172A",
                  fontFamily: "gilroySemiBold, Arial, sans-serif",
                  fontSize: "1.35rem",
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                {activeToast.title}
              </Typography>
              <IconButton
                size="small"
                aria-label="Close notification"
                onClick={handleCloseClick}
                sx={{
                  mt: "-5px",
                  mr: "-5px",
                  color: "#64748B",
                  flex: "0 0 auto",
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>

            <Typography
              sx={{
                mt: 0.45,
                color: "#334155",
                fontSize: "1.22rem",
                lineHeight: 1.45,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {activeToast.message}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.8}
              sx={{ mt: 1 }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#FF3B05",
                }}
              />
              <Typography
                sx={{
                  color: "#64748B",
                  fontFamily: "gilroySemiBold, Arial, sans-serif",
                  fontSize: "1.05rem",
                }}
              >
                {activeToast.time || "Just now"}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Alert>
    </Snackbar>
  );
};

export default GlobalNotificationToast;
